import React, { useEffect, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useNavigate } from "react-router-dom";
import { Camera, RefreshCcw, Trash2, Send, LoaderCircle } from "lucide-react";

const mainContainerStyle = {
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  background: "linear-gradient(to bottom, #eff6ff, #ffffff, #eff6ff)",
  padding: "1rem",
  fontFamily: "sans-serif",
};
if (typeof window !== "undefined" && window.innerWidth >= 640) {
  mainContainerStyle.padding = "1.5rem";
}

const headerStyle = { textAlign: "center", marginBottom: "1.5rem" };
const titleStyle = { fontSize: "1.5rem", fontWeight: "bold", color: "#1e3a8a", marginBottom: "0.25rem" };
if (typeof window !== "undefined" && window.innerWidth >= 640) {
  titleStyle.fontSize = "1.875rem";
}

const batchNumberStyle = { fontSize: "1.125rem", fontWeight: "600", color: "#374151" };
if (typeof window !== "undefined" && window.innerWidth >= 640) {
  batchNumberStyle.fontSize = "1.25rem";
}

const batchNumberSpanStyle = { fontFamily: "monospace", color: "#1d4ed8" };
const stepStyle = { fontSize: "0.875rem", color: "#6b7280" };
const imageSectionStyle = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexGrow: 1, marginBottom: "1.5rem" };
const captureLabelStyle = { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "0.75rem 1.5rem", backgroundColor: "#2563eb", color: "white", borderRadius: "9999px", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)", cursor: "pointer", transition: "background-color 150ms ease-in-out" };
const captureTextStyle = { fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" };
const imagePreviewContainerStyle = { display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: "20rem" };
if (typeof window !== "undefined" && window.innerWidth >= 640) {
  imagePreviewContainerStyle.maxWidth = "24rem";
}

const imagePreviewStyle = { width: "100%", height: "auto", maxHeight: "16rem", objectFit: "contain", borderRadius: "0.5rem", boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)", marginBottom: "1rem", border: "1px solid #e5e7eb" };
const imageActionsContainerStyle = { display: "flex", justifyContent: "center", gap: "0.75rem", width: "100%" };
const smallButtonStyle = { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", padding: "0.5rem 1rem", color: "white", fontSize: "0.875rem", borderRadius: "9999px", boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)", border: "none", cursor: "pointer", transition: "background-color 150ms ease-in-out" };
const recaptureButtonStyle = { ...smallButtonStyle, backgroundColor: "#f59e0b" };
const removeButtonStyle = { ...smallButtonStyle, backgroundColor: "#ef4444" };
const submissionSectionStyle = { marginTop: "auto", paddingTop: "1rem", borderTop: "1px solid #e5e7eb" };
const submitButtonStyleBase = { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "0.75rem 1.5rem", borderRadius: "9999px", fontSize: "1.125rem", fontWeight: "600", color: "white", boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)", transition: "background 150ms ease-in-out", border: "none", cursor: "pointer" };
const submitButtonStyleActive = { ...submitButtonStyleBase, background: "linear-gradient(to right, #22c55e, #059669)" };
const submitButtonStyleDisabled = { ...submitButtonStyleBase, backgroundColor: "#9ca3af", cursor: "not-allowed" };
const loadingContainerStyle = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "#f3f4f6", color: "#4b5563" };
const loadingIconStyle = { marginBottom: "1rem" };

function QRReader() {
  const [title, setTitle] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [skuid, setSkuid] = useState("");
  const [index, setIndex] = useState("");
  const [loading, setLoading] = useState(true);
  const [journeyNumber, setJourneyNumber] = useState(null);
  const [batchJourneyNumber, setBatchJourneyNumber] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const urlPath = window.location.pathname;
    const skuidWithIndex = decodeURIComponent(urlPath.split("/").pop() || "");
    const [skuidParam, indexParam] = skuidWithIndex.split("×");
    if (skuidParam && indexParam) {
      setSkuid(skuidParam);
      setIndex(indexParam);
      setJourneyNumber(parseInt(indexParam, 10));
      fetchProductJourney(skuidParam, indexParam);
    } else {
      setTitle("Invalid URL parameters.");
      setLoading(false);
    }
  }, []);

  const fetchProductJourney = async (skuidParam, indexParam) => {
    try {
      const response = await axios.get(`https://ezeclit8vi.execute-api.ap-south-1.amazonaws.com/prod/product-journey?skuid=${skuidParam}`);
      if (response.data && response.data.ProductJourney && response.data.ProductJourney.length > 0) {
        const journey = response.data.ProductJourney.find(item => item.index === parseInt(indexParam, 10));
        if (journey) {
          setTitle(journey.title);
          fetchBatchNumber(skuidParam, journey.index);
        } else {
          setTitle("No product journey title found.");
          setLoading(false);
        }
      } else {
        setTitle("No product journey found.");
        setLoading(false);
      }
    } catch (error) {
      setTitle("Error fetching product journey.");
      setLoading(false);
    }
  };

  const fetchBatchNumber = async (skuidParam, journeyNum) => {
    try {
      // 1️⃣ Try the traceability API first
      const traceRes = await axios.get(
        `https://hik28u3ab1.execute-api.ap-south-1.amazonaws.com/prod/traceability?skuid=${skuidParam}`
      );
      const traceData = traceRes.data;

      if (Array.isArray(traceData) && traceData.length > 0) {
        // find or increment the batch
        let latest = traceData[0].BatchNumber;
        let newBatch = true;
        traceData.forEach(entry => {
          if (entry.JourneyNumber < journeyNum && entry.Status !== "Completed") {
            latest = entry.BatchNumber;
            newBatch = false;
          }
        });
        if (newBatch) {
          latest = incrementBatchNumber(traceData[traceData.length - 1].BatchNumber);
        }
        setBatchNumber(latest);

        const matched = traceData.find(e => e.BatchNumber === latest);
        if (matched) setBatchJourneyNumber(matched.JourneyNumber);

      } else {
        // empty array → force fallback
        throw { fallback: true };
      }

    } catch (err) {
      // 2️⃣ On 404 or forced fallback, call product-journey
      const shouldFallback =
        (axios.isAxiosError(err) && err.response?.status === 404) ||
        err.fallback;

      if (shouldFallback) {
        try {
          const pjRes = await axios.get(
            `https://ezeclit8vi.execute-api.ap-south-1.amazonaws.com/prod/product-journey?skuid=${skuidParam}`
          );
          if (pjRes.data?.BatchNumber) {
            setBatchNumber(pjRes.data.BatchNumber);
          } else {
            setBatchNumber("No batch found");
          }
        } catch {
          setBatchNumber("Error");
        }
      } else {
        // some other error
        setBatchNumber("Error");
      }
    } finally {
      setLoading(false);
    }
  };

  const incrementBatchNumber = batchNum => {
    const match = batchNum.match(/\d+$/);
    if (match) {
      const numericPart = parseInt(match[0], 10) + 1;
      return batchNum.replace(/\d+$/, String(numericPart));
    }
    return `${batchNum}1`;
  };

  const handleSubmit = async () => {
    if (journeyNumber !== 1 && batchJourneyNumber !== journeyNumber - 1) {
      alert("Please complete the previous process.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`https://hik28u3ab1.execute-api.ap-south-1.amazonaws.com/prod/traceability`, {
        skuid,
        processNumber: index,
        imageBase64: capturedImage ? capturedImage.split(',')[1] : null,
      });
      if (response.data && response.data.message) {
        alert(response.data.message);
        if (response.data.message === "Product journey saved successfully") {
          navigate("/complete", { replace: true });
        }
      } else {
        alert("Error saving product journey.");
      }
    } catch (error) {
      alert("Error saving product journey.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = () => {
    const userConfirmed = window.confirm("Are you sure you want to Capture this Process?");
    if (userConfirmed) handleSubmit();
  };

  const handleCapture = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1024, useWebWorker: true, initialQuality: 0.7 };
      const compressedFile = await imageCompression(file, options);
      const reader = new FileReader();
      reader.onloadend = () => setCapturedImage(reader.result);
      reader.readAsDataURL(compressedFile);
    } catch {
      alert("Failed to compress image.");
    }
  };

  const handleRecapture = () => {
    setCapturedImage(null);
    document.getElementById("captureInput")?.click();
  };

  const handleRemove = () => setCapturedImage(null);

  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <div style={loadingIconStyle}>
          <LoaderCircle size={48} color="#2563eb" style={{ animation: "spin 1s linear infinite" }} />
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
        <p>Loading Process Details...</p>
      </div>
    );
  }

  return (
    <div style={mainContainerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>{title || "Process Step"}</h1>
        {batchNumber && (<p style={batchNumberStyle}>Batch: <span style={batchNumberSpanStyle}>{batchNumber}</span></p>)}
        <p style={stepStyle}>Step {journeyNumber}</p>
      </div>
      <div style={imageSectionStyle}>
        {!capturedImage ? (
          <>
            <input type="file" accept="image/*" capture="environment" onChange={handleCapture} style={{ display: "none" }} id="captureInput" />
            <label htmlFor="captureInput" style={captureLabelStyle}><Camera size={20} /><span>Capture Image</span></label>
            <p style={captureTextStyle}>Tap to open camera</p>
          </>
        ) : (
          <div style={imagePreviewContainerStyle}>
            <img src={capturedImage} alt="Captured" style={imagePreviewStyle} />
            <div style={imageActionsContainerStyle}>
              <button onClick={handleRecapture} type="button" style={recaptureButtonStyle}><RefreshCcw size={16} /><span>Recapture</span></button>
              <button onClick={handleRemove} type="button" style={removeButtonStyle}><Trash2 size={16} /><span>Remove</span></button>
            </div>
          </div>
        )}
      </div>
      <div style={submissionSectionStyle}>
        <button onClick={handleButtonClick} disabled={isSubmitting} style={isSubmitting ? submitButtonStyleDisabled : submitButtonStyleActive}>
          {isSubmitting ? (<><LoaderCircle size={20} style={{ animation: "spin 1s linear infinite" }} /><span>Submitting...</span></>) : (<><Send size={20} /><span>Submit Process</span></>)}
        </button>
      </div>
    </div>
  );
}

export default QRReader;